/**
 * Data-driven vehicle definitions.
 *
 * Adding a car never requires touching the physics code: a car is a single
 * `CarDef` describing its identity, its UI stats and its `CarHandling`
 * parameters. `makeHandling` fills in physically coherent defaults so a new
 * car only has to specify what makes it special.
 */

import type { SurfaceId } from './surfaces.js';

export type DriveType = 'fwd' | 'rwd' | 'awd';
export type CarClass = 'hatch' | 'tuner' | 'muscle' | 'rally' | 'drift' | 'super' | 'offroad' | 'kart';

export interface CarHandling {
  /** Mass in kg-equivalent units. Affects acceleration, collisions and yaw inertia. */
  mass: number;
  /** Multiplier on the theoretical yaw inertia (higher = lazier rotation). */
  inertiaScale: number;
  /** Body length/width used for rendering and collision hulls (world px). */
  length: number;
  width: number;
  /** Axle separation (world px). Longer wheelbases are more stable. */
  wheelBase: number;
  /** Centre-of-mass height; drives longitudinal weight transfer. */
  comHeight: number;
  /** Static weight fraction resting on the front axle (0..1). */
  frontWeight: number;
  /** Peak drive force at full throttle (N). */
  engineForce: number;
  /** Peak braking force (N). */
  brakeForce: number;
  /** Aerodynamic/limiter ceiling (px/s). */
  maxSpeed: number;
  reverseMaxSpeed: number;
  /** Drag coefficient: terminal speed is sqrt(engineForce / drag). */
  drag: number;
  /** Rolling resistance expressed as a deceleration (px/s^2). */
  rollResist: number;
  /** Maximum steer angle at standstill (rad). */
  steerMax: number;
  /** How much the steer limit shrinks at top speed (0..1). */
  steerFalloff: number;
  /** How quickly the steering input drives the wheels (rad/s). */
  steerRate: number;
  /** Cornering stiffness coefficient: lateral force per radian per unit load. */
  cornerStiffness: number;
  /** Base tyre friction coefficient. */
  muBase: number;
  /** Rear friction multiplier while the handbrake is held. */
  handbrakeMu: number;
  /** Extra friction coefficient gained at top speed (aerodynamic downforce). */
  downforce: number;
  drive: DriveType;
  /** 0 = all drive to the rear, 1 = all drive to the front (awd blend). */
  driveBias: number;
  /** Additive friction bonus per surface, e.g. rally cars get +0.8 on gravel. */
  gripBonus: Partial<Record<SurfaceId, number>>;
  /** Torque scale after gear changes (automatic gearbox flavour). */
  gearShiftTime: number;
  /** Nitrous / turbo force bonus (N) and duration in seconds. */
  turboForce: number;
  turboTime: number;
  /** How quickly steering self-centres when no input is given (rad/s). */
  steerReturn: number;
}

export interface CarStats {
  accel: number;
  topSpeed: number;
  handling: number;
  grip: number;
  braking: number;
  weight: number;
  durability: number;
}

export interface CarDef {
  id: string;
  name: string;
  carClass: CarClass;
  description: string;
  price: number;
  /** Car that must be owned before this one can be bought (garage gating). */
  requires?: string;
  stats: CarStats;
  handling: CarHandling;
  /** Body colours offered in the garage (player picks one). */
  palettes: string[];
  /** Silhouette hint for the pixel-art renderer. */
  sprite: CarClass;
  /** Optional achievement gate shown in the garage. */
  unlockNote?: string;
}

const GRAVITY = 147; // px/s^2 (≈9.81 m/s² at 15 px/m)

/**
 * Fraction of peak drive force still available at the speed limiter. The
 * engine torque curve fades with speed, so drag is derived from this value to
 * make `maxSpeed` the true terminal velocity of the car.
 */
export const POWER_AT_TOP = 0.65;

/**
 * Builds a `CarHandling` record from a partial spec, deriving the physical
 * values that follow from the explicitly provided ones.
 */
export function makeHandling(spec: Partial<CarHandling> & { mass: number; engineForce: number; maxSpeed: number }): CarHandling {
  const mass = spec.mass;
  const maxSpeed = spec.maxSpeed;
  const wheelBase = spec.wheelBase ?? 24;
  const length = spec.length ?? 36;
  const width = spec.width ?? 20;
  const frontWeight = spec.frontWeight ?? 0.5;
  return {
    mass,
    inertiaScale: spec.inertiaScale ?? 1,
    length,
    width,
    wheelBase,
    comHeight: spec.comHeight ?? 5,
    frontWeight,
    engineForce: spec.engineForce,
    brakeForce: spec.brakeForce ?? mass * 165,
    maxSpeed,
    reverseMaxSpeed: spec.reverseMaxSpeed ?? maxSpeed * 0.35,
    // Terminal-velocity consistent drag: the engine still makes POWER_AT_TOP
    // of its peak force at `maxSpeed`, so that is the force drag must balance.
    drag: spec.drag ?? (spec.engineForce * POWER_AT_TOP) / (maxSpeed * maxSpeed),
    rollResist: spec.rollResist ?? 9,
    steerMax: spec.steerMax ?? 0.62,
    steerFalloff: spec.steerFalloff ?? 0.62,
    steerRate: spec.steerRate ?? 3.6,
    steerReturn: spec.steerReturn ?? 5.5,
    cornerStiffness: spec.cornerStiffness ?? 9,
    muBase: spec.muBase ?? 1.65,
    handbrakeMu: spec.handbrakeMu ?? 0.32,
    downforce: spec.downforce ?? 0.25,
    drive: spec.drive ?? 'rwd',
    driveBias: spec.driveBias ?? 0,
    gripBonus: spec.gripBonus ?? {},
    gearShiftTime: spec.gearShiftTime ?? 0.22,
    turboForce: spec.turboForce ?? mass * 55,
    turboTime: spec.turboTime ?? 2.2,
  };
}

export const CAR_GRAVITY = GRAVITY;

export const CARS: CarDef[] = [
  {
    id: 'pico',
    name: 'Pico Hatch',
    carClass: 'hatch',
    sprite: 'hatch',
    description: 'Friendly front-drive hatch. Forgiving, balanced and cheap to run — the perfect first car.',
    price: 0,
    stats: { accel: 4, topSpeed: 4, handling: 5, grip: 5, braking: 5, weight: 4, durability: 6 },
    palettes: ['#e8453c', '#f2c14e', '#4fa3e8', '#66c46a', '#e8e3d8', '#8e6fd6'],
    handling: makeHandling({
      mass: 980, engineForce: 88000, maxSpeed: 322, wheelBase: 23, length: 34, width: 19,
      frontWeight: 0.6, drive: 'fwd', muBase: 1.7, steerMax: 0.66, rollResist: 9,
      gripBonus: { grass: 0.06, dirt: 0.05 },
    }),
  },
  {
    id: 'kart',
    name: 'Pocket Kart',
    carClass: 'kart',
    sprite: 'kart',
    description: 'Featherweight tin-can with a screaming engine. Devastating in tight corners, terrifying on straights.',
    price: 4200,
    stats: { accel: 7, topSpeed: 3, handling: 9, grip: 6, braking: 6, weight: 2, durability: 3 },
    palettes: ['#ff7a29', '#7ce8ff', '#ff4d9d', '#c8ff4d', '#ffffff', '#23252b'],
    handling: makeHandling({
      mass: 620, engineForce: 66000, maxSpeed: 300, wheelBase: 20, length: 30, width: 20,
      frontWeight: 0.45, drive: 'rwd', muBase: 1.8, steerMax: 0.72, steerRate: 4.6, rollResist: 7,
      downforce: 0.12, inertiaScale: 0.85,
    }),
  },
  {
    id: 'zenith',
    name: 'Zenith Tuner',
    carClass: 'tuner',
    sprite: 'tuner',
    description: 'Lightweight rear-drive coupe. Explosive acceleration and razor-sharp turn-in.',
    price: 13500,
    stats: { accel: 7, topSpeed: 6, handling: 8, grip: 7, braking: 7, weight: 4, durability: 5 },
    palettes: ['#31d7c8', '#f5f5f5', '#ff3b30', '#3f6cff', '#ffd23f', '#1b1d22'],
    handling: makeHandling({
      mass: 1080, engineForce: 118000, maxSpeed: 386, wheelBase: 25, length: 37, width: 20,
      frontWeight: 0.52, drive: 'rwd', muBase: 1.78, steerMax: 0.6, cornerStiffness: 9.5,
      gripBonus: { gravel: 0.08, dirt: 0.06 },
    }),
  },
  {
    id: 'vulcan',
    name: 'Vulcan GT',
    carClass: 'muscle',
    sprite: 'muscle',
    description: 'Big-block muscle car. Enormous power, heavy nose, loves a long powerslide.',
    price: 27000,
    stats: { accel: 7, topSpeed: 8, handling: 4, grip: 5, braking: 5, weight: 8, durability: 8 },
    palettes: ['#ff4d2e', '#2b2f36', '#f0c419', '#2f8f4e', '#8b1e3f', '#d9d9d9'],
    handling: makeHandling({
      mass: 1560, engineForce: 190000, maxSpeed: 432, wheelBase: 29, length: 40, width: 22,
      frontWeight: 0.56, drive: 'rwd', muBase: 1.6, steerMax: 0.55, inertiaScale: 1.25,
      rollResist: 11, cornerStiffness: 8, comHeight: 6,
    }),
  },
  {
    id: 'dustback',
    name: 'Dustback Rally',
    carClass: 'rally',
    sprite: 'rally',
    description: 'All-wheel-drive rally weapon. Loose surfaces barely slow it down.',
    price: 34000,
    stats: { accel: 8, topSpeed: 6, handling: 7, grip: 8, braking: 7, weight: 6, durability: 9 },
    palettes: ['#2f8f4e', '#e8e3d8', '#ff8c1a', '#2f6cb5', '#c81e3a', '#4a4f57'],
    handling: makeHandling({
      mass: 1240, engineForce: 152000, maxSpeed: 392, wheelBase: 26, length: 38, width: 22,
      frontWeight: 0.53, drive: 'awd', driveBias: 0.35, muBase: 1.85, steerMax: 0.62,
      gripBonus: { gravel: 0.85, dirt: 0.8, mud: 0.6, sand: 0.5, grass: 0.55, ice: 0.2 },
      rollResist: 8.5, downforce: 0.22,
    }),
  },
  {
    id: 'ridgeback',
    name: 'Ridgeback 4x4',
    carClass: 'offroad',
    sprite: 'offroad',
    description: 'Lifted truck that shrugs off sand, mud and rocks. Slow on tarmac, unstoppable everywhere else.',
    price: 41000,
    stats: { accel: 5, topSpeed: 5, handling: 5, grip: 7, braking: 6, weight: 9, durability: 10 },
    palettes: ['#c96a1e', '#3d4d33', '#e0d9c8', '#2f6cb5', '#7a2f2f', '#c8c8c8'],
    handling: makeHandling({
      mass: 1780, engineForce: 172000, maxSpeed: 352, wheelBase: 31, length: 42, width: 24,
      frontWeight: 0.55, drive: 'awd', driveBias: 0.45, muBase: 1.7, steerMax: 0.58,
      gripBonus: { gravel: 0.9, dirt: 0.85, mud: 0.75, sand: 0.95, grass: 0.6, ice: 0.25 },
      rollResist: 12, comHeight: 7, inertiaScale: 1.2, downforce: 0.1,
    }),
  },
  {
    id: 'sideways',
    name: 'Sideways S',
    carClass: 'drift',
    sprite: 'drift',
    description: 'Purpose-built drift machine. Sticky front axle, greasy rear — slides on command.',
    price: 48000,
    stats: { accel: 8, topSpeed: 7, handling: 9, grip: 5, braking: 7, weight: 5, durability: 5 },
    palettes: ['#ff2d78', '#1de9b6', '#ffd23f', '#8e6fd6', '#f5f5f5', '#16181d'],
    handling: makeHandling({
      mass: 1120, engineForce: 168000, maxSpeed: 402, wheelBase: 26, length: 38, width: 21,
      frontWeight: 0.5, drive: 'rwd', muBase: 1.55, handbrakeMu: 0.16, steerMax: 0.74,
      steerRate: 4.2, cornerStiffness: 10.5, inertiaScale: 0.95, comHeight: 4.5,
    }),
  },
  {
    id: 'apogee',
    name: 'Apogee X',
    carClass: 'super',
    sprite: 'super',
    description: 'Mid-engined hypercar. Blistering top speed and grip — demands precision at the limit.',
    price: 96000,
    requires: 'zenith',
    unlockNote: 'Requires the Zenith Tuner',
    stats: { accel: 10, topSpeed: 10, handling: 9, grip: 10, braking: 10, weight: 6, durability: 5 },
    palettes: ['#e0e0e0', '#0f1013', '#c8102e', '#0a84ff', '#f5c518', '#2fbf71'],
    handling: makeHandling({
      mass: 1290, engineForce: 214000, maxSpeed: 508, wheelBase: 28, length: 41, width: 23,
      frontWeight: 0.44, drive: 'awd', driveBias: 0.22, muBase: 2.0, steerMax: 0.56,
      downforce: 0.55, cornerStiffness: 10, rollResist: 10, brakeForce: 1290 * 195,
      turboForce: 1290 * 78, turboTime: 2.6,
    }),
  },
];

export const CAR_BY_ID: Record<string, CarDef> = CARS.reduce(
  (acc, car) => {
    acc[car.id] = car;
    return acc;
  },
  {} as Record<string, CarDef>,
);

export const STARTER_CAR_ID = 'pico';

export function getCar(id: string): CarDef {
  const car = CAR_BY_ID[id];
  if (!car) throw new Error(`Unknown car id: ${id}`);
  return car;
}
